import { Box, Dialog, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { tokens } from '../../../global/theme/tokens';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import TimeDetailsViewer from './time-components/TimeDetailsViewer';
import TimeSelectionBox from './time-components/TimeSelectionBox';
import { timesOverlapping } from './time-functions/overlappingTimes';
import WeeklyHour from './WeeklyHour';
import WeeklyEventBox from './WeeklyEventBox';
import { ExoCalPopper } from '../cal/ExoCalPopper';
import Feedback from '../../special/Feedback';
import { ExoCalWholeDayEvents } from './ExoCalWholeDayEvents.tsx';

const WeeklyColumn = ({
  onDateClick,
  dayLabelHeight,
  index,
  edit,
  day,
  onUpdate = () => {},
  onAdd = () => {},
  onDelete = () => {},
  exoWorkdayRef,
  snapToGrid,
  snapRatio,
  hourStart,
  hourEnd,
  res,
  resHeight,
  setEnableTouchScroll,
  noInfo,
  userId,
  labelFormat,
  onDoubleClick,
  onEventEdit,
  onEventSelect,
  calendars,
  offsetTop,
  selectedPeriod,
  onCreateEventInDayView,
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // size controll
  const boxRef = useRef(null);
  const [mobileView, setMobileView] = useState(false);
  const [smallScreen, SetSmallScreen] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [popperAnchor, setPopperAnchor] = useState(null);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const handleResize = () => {
    // Update containerWidth when the container is resized
    if (!boxRef.current) return;
    const width = boxRef.current.offsetWidth;

    setMobileView(width < 160);

    // Conditional logic for smallScreen
    if (selectedPeriod === 'day') {
      SetSmallScreen(width < 450); // Use this threshold for single-day column(Day view)
    } else {
      SetSmallScreen(width < 126); // Use small-screen threshold for multiple days columns(week view)
    }
  };

  useEffect(() => {
    // Attach event listener for window resize
    window.addEventListener('resize', handleResize);

    // Initial setup
    handleResize();

    // Cleanup the event listener when the component is unmounted
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [boxRef]);

  // selection and boxes
  const selectionRef = useRef(null);
  const dragBox = useRef(null);
  const [selection, setSelection] = useState({
    timeStart: null,
    timeEnd: null,
  });
  const [mouseIsDown, setMouseIsDown] = useState(false);
  const [handlerType, setHandlerType] = useState('lower');

  const handleMouseDown = (e, type = 'lower') => {
    if (!edit || smallScreen) return;
    setHandlerType(type);
    setMouseIsDown(true);
    e.preventDefault();
    if (selection.timeStart && selection.timeEnd) return;

    const mouseY = e.clientY;
    const dragBoxOffsetTop = dragBox.current.getBoundingClientRect().top;
    const startY =
      Math.round((mouseY - dragBoxOffsetTop) / resHeight) * resHeight;

    setSelection({
      timeStart: pixelToDate(startY),
      timeEnd: pixelToDate(startY),
    });
  };

  const handleMouseUp = (e) => {
    if (!edit) return;
    if (!mouseIsDown) return;
    setMouseIsDown(false);
    setHandlerType('upper');
    if (snapToGrid && selection.timeStart && selection.timeEnd)
      snapSelectionToGrid();
  };

  const handleMouseMove = (e) => {
    if (!edit) return;
    if (!mouseIsDown) return;
    const mouseY = e.clientY;
    const dragBoxOffsetTop = dragBox.current.getBoundingClientRect().top;

    var invert = selection.timeStart + '' > selection.timeEnd + '';

    if (handlerType === 'lower') {
      setSelection((prevSelection) => {
        if (invert) {
          setHandlerType('upper');
          return {
            ...prevSelection,
            timeStart: pixelToDate(mouseY - dragBoxOffsetTop),
          };
        }
        return {
          ...prevSelection,
          timeEnd: pixelToDate(mouseY - dragBoxOffsetTop),
        };
      });
    }
    if (handlerType === 'upper') {
      setSelection((prevSelection) => {
        if (invert) {
          setHandlerType('lower');
          return {
            ...prevSelection,
            timeEnd: pixelToDate(mouseY - dragBoxOffsetTop),
          };
        }
        return {
          ...prevSelection,
          timeStart: pixelToDate(mouseY - dragBoxOffsetTop),
        };
      });
    }
    if (handlerType === 'move') {
      setSelection((prevSelection) => ({
        ...prevSelection,
        timeEnd: pixelToDate(dateToPixel(prevSelection.timeEnd) + e.movementY),
        timeStart: pixelToDate(
          dateToPixel(prevSelection.timeStart) + e.movementY
        ),
      }));
    }
  };

  const handleTouchStart = (e, type = 'lower') => {
    if (!edit || smallScreen || selectedPeriod === 'day') return;
    setHandlerType(type);
    setMouseIsDown(true);
    setEnableTouchScroll(false);
    if (selection.timeStart && selection.timeEnd) return;

    const touchY = e.touches[0].clientY + exoWorkdayRef.current.scrollTop;
    const dragBoxOffsetTop = dragBox.current.getBoundingClientRect().top;
    const startY =
      Math.round((touchY - dragBoxOffsetTop) / resHeight) * resHeight;

    setSelection({
      timeStart: pixelToDate(startY),
      timeEnd: pixelToDate(startY),
    });
  };

  const handleTouchEnd = (e) => {
    if (!edit) return;
    if (!mouseIsDown) return;
    setEnableTouchScroll(true);
    setMouseIsDown(false);
    setHandlerType('upper');
    if (snapToGrid) snapSelectionToGrid();
  };

  const [prevTouchY, setprevTouchY] = useState(0);
  const handleTouchMove = (e) => {
    if (!edit) return;
    if (!mouseIsDown) return;
    const touchY = e.touches[0].clientY + exoWorkdayRef.current.scrollTop;
    var deltaY = 0;
    if (prevTouchY) {
      const height = selectionRef?.current?.offsetHeight;
      if (handlerType === 'lower') {
        setprevTouchY(touchY - height / 2);
      }
      if (handlerType === 'upper') {
        setprevTouchY(touchY + height / 2);
      }
      if (handlerType === 'move') {
        setprevTouchY(touchY);
      }
      deltaY = touchY - prevTouchY;
    } else {
      setprevTouchY(touchY);
    }

    const dragBoxOffsetTop = dragBox.current.getBoundingClientRect().top;
    var invert = selection.timeStart + '' > selection.timeEnd + '';

    if (handlerType === 'lower') {
      setSelection((prevSelection) => {
        if (invert) {
          setHandlerType('upper');
          return {
            ...prevSelection,
            timeStart: pixelToDate(touchY - dragBoxOffsetTop),
          };
        }
        return {
          ...prevSelection,
          timeEnd: pixelToDate(touchY - dragBoxOffsetTop),
        };
      });
    }
    if (handlerType === 'upper') {
      setSelection((prevSelection) => {
        if (invert) {
          setHandlerType('lower');
          return {
            ...prevSelection,
            timeEnd: pixelToDate(touchY - dragBoxOffsetTop - offsetTop),
          };
        }
        return {
          ...prevSelection,
          timeStart: pixelToDate(touchY - dragBoxOffsetTop),
        };
      });
    }
    if (handlerType === 'move') {
      setSelection((prevSelection) => ({
        ...prevSelection,
        timeEnd: pixelToDate(dateToPixel(prevSelection.timeEnd) + deltaY),
        timeStart: pixelToDate(dateToPixel(prevSelection.timeStart) + deltaY),
      }));
    }
  };

  //pixeltoDate and dateToPixel functions
  function pixelToDate(pixel) {
    const numOfSegments = (pixel - offsetTop) / resHeight;
    const minutesPerSegment = 60 / res;
    const startMinutes = hourStart * 60;
    const minutes =
      numOfSegments * minutesPerSegment + startMinutes - minutesPerSegment;

    const dateTime = day.date.clone().add(minutes, 'minutes').toISOString(); // FIXED

    return dateTime;
  }

  function dateToPixel(date) {
    const minutesPerSegment = 60 / res;

    const startDate = moment(date);
    var minutes =
      startDate.hours() * 60 + startDate.minutes() + startDate.seconds() / 60;

    const startMinutes = hourStart * 60;
    // limit the upper end
    if (startMinutes >= minutes) minutes = startMinutes;

    const endMinutes = hourEnd * 60;
    // limit the lower end
    if (endMinutes <= minutes) minutes = endMinutes;

    const numOfSegments = (minutes - startMinutes) / minutesPerSegment;
    const pixel = numOfSegments * resHeight + resHeight + offsetTop;
    return pixel;
  }

  function snapSelectionToGrid() {
    if (timesOverlapping(day.events, selection))
      return snapToOverlappingSection(timesOverlapping(day.events, selection));
    var pixelEnd = dateToPixel(selection.timeEnd);
    const pixelStart = dateToPixel(selection.timeStart);
    if (pixelEnd - pixelStart < resHeight) pixelEnd += resHeight;

    const snappedDateStart = pixelToDate(snapPixel(pixelStart));
    const snappedDateEnd = pixelToDate(snapPixel(pixelEnd));
    if (
      timesOverlapping(day.events, {
        ...selection,
        timeStart: snappedDateStart,
        timeEnd: snappedDateEnd,
      })
    )
      return snapToOverlappingSection(
        timesOverlapping(day.events, {
          ...selection,
          timeStart: snappedDateStart,
          timeEnd: snappedDateEnd,
        })
      );
    setSelection({
      ...selection,
      timeStart: snappedDateStart,
      timeEnd: snappedDateEnd,
    });
  }

  function snapToOverlappingSection({ time1, time2 }) {
    // time2 is the selection
    var selectedEvent = time2;
    if (
      selectedEvent.timeStart < time1.timeStart &&
      selectedEvent.timeEnd > time1.timeStart
    )
      selectedEvent.timeEnd = time1.timeStart;

    if (
      selectedEvent.timeStart < time1.timeEnd &&
      selectedEvent.timeEnd > time1.timeEnd
    )
      selectedEvent.timeStart = time1.timeEnd;

    if (
      selectedEvent.timeStart >= time1.timeStart &&
      selectedEvent.timeEnd <= time1.timeEnd
    ) {
      setSelection({
        timeStart: null,
        timeEnd: null,
      });
    }
  }

  function snapPixel(pixel) {
    const snappedPixels =
      Math.round((pixel - offsetTop) / (resHeight / snapRatio)) *
      (resHeight / snapRatio);
    return snappedPixels + offsetTop;
  }

  // check for day view and then create event accordingly
  function handleSubmit(e) {
    if (e) setPopperAnchor(e.currentTarget);
    setOpen(!timesOverlapping(day.events, selection));
    // if (selectedPeriod !== 'day') {
    // setOpen(!timesOverlapping(day.events, selection));
    // } else {
    //   onCreateEventInDayView({
    //     startDateTime: selection.timeStart,
    //     endDateTime: selection.timeEnd,
    //     wholeDay: false,
    //   });
    // }
  }

  // modification
  const [open, setOpen] = useState(false);

  function handleUpdate(data) {
    if (timesOverlapping(day.events, selection)) return;
    onUpdate(data);
    setSelection({});
    setOpen(false);
  }

  function handleDelete() {
    if (selection.id) {
      onDelete({ ...selection, method: 'delete' });
      setSelection({});
    }
  }

  function handleCancel() {
    setSelection({});
    setOpen(false);
    onClose();
  }

  function handleEdit() {
    setOpen(true);
  }

  // check if its today
  const today = day.date.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');

  const heighlightColor = today ? colors.info + '30' : colors.card;

  const dayLabel = day.date.format(labelFormat);

  const wholeDayEvents = day.events.filter((e) => e.wholeDay);
  const timedEvents = day.events.filter((e) => !e.wholeDay);

  const fillEmptyLayers = (events) => {
    if (events.length === 0) return [];
    // 1️⃣ Sortiere alle Events nach Layer (aufsteigend)
    const sortedEvents = [...events].sort((a, b) => a.layer - b.layer);

    // 2️⃣ Bestimme die höchste belegte Layer-Nummer
    const highestLayer = sortedEvents.length
      ? Math.max(...sortedEvents.map((e) => e.layer))
      : 0;

    // 3️⃣ Erstelle eine Map für existierende Layer
    const existingLayers = new Set(sortedEvents.map((e) => e.layer));

    // 4️⃣ Fülle leere Layer mit Platzhalter-Events
    for (let i = 0; i <= highestLayer; i++) {
      if (!existingLayers.has(i)) {
        sortedEvents.push({
          id: `empty-${i}`,
          layer: i,
          isPlaceholder: true, // ⚠ Platzhalter-Event zur UI-Erkennung
        });
      }
    }
    // 5️⃣ Erneut nach Layer sortieren (da wir neue Elemente hinzugefügt haben)
    return sortedEvents.sort((a, b) => a.layer - b.layer);
  };

  //For mobile view quick create events
  function handleQuickCreate(e) {
    if (!smallScreen || !edit) return;

    const y = e.clientY || (e.touches?.[0]?.clientY ?? 0);
    const dragBoxOffsetTop = dragBox.current.getBoundingClientRect().top;
    const yRelative = y - dragBoxOffsetTop;

    const snappedY = Math.round(yRelative / resHeight) * resHeight;

    const startTime = pixelToDate(snappedY);
    const endTime = pixelToDate(snappedY + resHeight * 2); // e.g. 30-min event

    setSelection({ timeStart: startTime, timeEnd: endTime });
    setOpen(true); // Show the ExoCalPopper
  }

  return (
    <Box className="w-full " ref={boxRef}>
      <Box
        className="w-full sticky top-0 left-0 z-10 px-1 flex flex-col @container"
        sx={{
          height: `${dayLabelHeight}px`,
        }}
      >
        <Box
          className="rounded-lg w-full h-full transition-colors backdrop-blur-lg px-1 flex flex-col justify-center items-center overflow-hidden"
          sx={{
            bgcolor: heighlightColor,
            border: `1px solid ${today ? heighlightColor : 'transparent'}`,
          }}
        >
          <Typography
            variant="h4"
            textAlign="center"
            className=" whitespace-nowrap h-full w-full  justify-center items-center rounded-lg pt-1 flex"
            fontWeight={600}
          >
            {mobileView
              ? t(dayLabel).charAt(0) + t(dayLabel).charAt(1)
              : t(dayLabel)}
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            className=" whitespace-nowrap h-full w-full flex justify-center items-center rounded-lg"
          >
            {day.date.format('DD.MM') === 'Invalid date'
              ? ''
              : day.date.format('DD.MM')}
          </Typography>
        </Box>
      </Box>
      <Box
        className="block relative w-full"
        onDoubleClick={
          selectedPeriod !== 'month' ? handleQuickCreate : () => {}
        }
        onTouchMove={handleTouchMove}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        ref={dragBox}
      >
        <Box sx={{ minHeight: `${offsetTop}px` }}>
          {[...fillEmptyLayers(wholeDayEvents)] // Only include whole-day events
            .map((event, index) => (
              <ExoCalWholeDayEvents
                event={event}
                isFirst={moment(event.startDateTime).isSame(day.date, 'day')}
                isLast={moment(event.endDateTime).isSame(day.date, 'day')}
                isFirstInWeek={day.date.isoWeekday() === 1} // Monday
                key={event.id}
                onDateClick={onDateClick}
                userId={userId}
                onDoubleClick={onDoubleClick}
                onEventEdit={onEventEdit}
                onEventSelect={onEventSelect}
                calendars={calendars}
                onUpdate={onUpdate}
                selectedPeriod={selectedPeriod}
              />
            ))}
        </Box>
        {[...Array(hourEnd - hourStart)].map((e, i) => (
          <Box
            className="flex flex-row w-full"
            key={i}
            onMouseDown={selection.timeStart ? () => {} : handleMouseDown}
            onTouchStart={selection.timeStart ? () => {} : handleTouchStart}
          >
            <WeeklyHour
              hour={i + hourStart}
              hourEnd={hourEnd}
              res={res}
              resHeight={resHeight}
              noHours={Boolean(index === 0)}
            />
          </Box>
        ))}

        {timedEvents // Only include normal time events
          .map((event, index) => (
            <WeeklyEventBox
              key={event.id}
              event={event}
              dateToPixel={dateToPixel}
              onDoubleClick={onDoubleClick}
              onEventEdit={onEventEdit}
              onEventSelect={onEventSelect}
              isFirst={moment(event.startDateTime).isSame(day.date, 'day')}
              isLast={moment(event.endDateTime).isSame(day.date, 'day')}
              isFirstInWeek={day.date.isoWeekday() === 1} // Monday
              day={{ ...day, events: timedEvents }}
              offsetTop={offsetTop}
              selectedPeriod={selectedPeriod}
            />
          ))}
        {today && <CurrentTimeIndicator dateToPixel={dateToPixel} />}
        {!smallScreen && selection.timeStart && selection.timeEnd && (
          <TimeSelectionBox
            selectionRef={selectionRef}
            dateToPixel={dateToPixel}
            mouseIsDown={mouseIsDown}
            selection={selection}
            marginLeft={0}
            hourStart={hourStart}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            onEdit={handleEdit}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            snapSelectionToGrid={snapSelectionToGrid}
            offsetTop={offsetTop}
            mobileView={mobileView}
            smallScreen={smallScreen}
            onDoubleClick={handleSubmit}
            selectionPeriod={selectedPeriod}
          />
        )}
      </Box>

      <Feedback state={alertState} setState={setAlertState} />

      <ExoCalPopper
        anchorEl={popperAnchor}
        open={open}
        onClose={() => {
          setOpen(false);
          handleCancel();
        }}
        data={{
          startDateTime: selection.timeStart,
          endDateTime: selection.timeEnd,
          wholeDay: false,
        }}
        calendars={calendars}
        onFeedback={handleFeedback}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        editMode={editMode}
        setEditMode={setEditMode}
      />
    </Box>
  );
};

export default WeeklyColumn;

const CurrentTimeIndicator = ({ dateToPixel }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [position, setPosition] = useState(dateToPixel(moment().toISOString()));
  const [timeString, setTimeString] = useState(moment().format('HH:mm'));

  useEffect(() => {
    const interval = setInterval(() => {
      setPosition(dateToPixel(moment().toISOString()));
      setTimeString(moment().format('HH:mm'));
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [dateToPixel]);

  return (
    <Box
      className="w-full flex gap-1 px-2 absolute left-0 h-[0px] items-center z-[2]"
      sx={{ top: position + 'px', pointerEvents: 'none' }}
    >
      <Box
        sx={{ bgcolor: colors.info, height: '2px' }}
        className="w-full rounded-full"
      />
      <Typography sx={{ color: colors.info }} fontSize={12} fontWeight={500}>
        {timeString}
      </Typography>
      <Box
        sx={{ bgcolor: colors.info, height: '2px' }}
        className="w-full rounded-full"
      />
    </Box>
  );
};
